import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { titleCase, StatusBadge } from "@/lib/format";
import { PageHeader, DataTable, useList } from "@/components/shared";
import { FormDialog } from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { Package, Plus, Edit } from "lucide-react";

const prep = (v) => ({ ...v, public_lender_id: v.public_lender_id === "none" ? null : v.public_lender_id, documents: typeof v.documents === "string" ? v.documents.split(",").map((s) => s.trim()).filter(Boolean) : v.documents });

export default function LoanProducts() {
  const { hasPerm } = useAuth();
  const { items, loading, refetch } = useList("/loan/products", {});
  const [lenders, setLenders] = useState([]);
  useEffect(() => { api.get("/loan/lenders").then(({ data }) => setLenders(data.filter((l) => l.public_enabled))).catch(() => {}); }, []);
  const create = async (v) => { await api.post("/loan/products", prep(v)); refetch(); };
  const edit = async (p, v) => { await api.put(`/loan/products/${p.id}`, prep(v)); refetch(); };
  const fields = [
    { name: "name", label: "Product Name", required: true },
    { name: "category", label: "Category", type: "select", options: ["personal", "business", "lap", "home", "vehicle", "two_wheeler", "education", "gold", "credit_line", "other"].map((s) => ({ value: s, label: titleCase(s) })) },
    { name: "lender", label: "Default Lender (optional)" },
    { name: "public_lender_id", label: "Website redirect lender (only lenders shown on public page)", type: "select", options: [{ value: "none", label: "None — team calls back" }, ...lenders.map((l) => ({ value: l.id, label: l.name }))] },
    { name: "eligibility", label: "Eligibility Requirements", type: "textarea", full: true },
    { name: "documents", label: "Required Documents (comma-separated — auto-fills application checklist)", type: "textarea", full: true },
    { name: "commission_terms", label: "Commission Terms" },
    { name: "workflow", label: "Application Workflow Notes", type: "textarea", full: true },
  ];
  const columns = [
    { key: "name", header: "Product", render: (r) => <span className="font-semibold">{r.name}</span> },
    { key: "category", header: "Category", render: (r) => <span className="text-sm">{titleCase(r.category)}</span> },
    { key: "documents", header: "Required Docs", render: (r) => <span className="text-sm text-muted-foreground">{(r.documents || []).join(", ") || "—"}</span> },
    { key: "commission_terms", header: "Commission Terms", render: (r) => <span className="text-sm text-muted-foreground">{r.commission_terms || "—"}</span> },
    { key: "active", header: "Status", render: (r) => <StatusBadge value={r.active ? "active" : "lost"} /> },
    ...(hasPerm("loan_products:edit") ? [{ key: "actions", header: "", render: (r) => (
      <div onClick={(e) => e.stopPropagation()}>
        <FormDialog title={`Edit ${r.name}`} fields={fields} initial={{ ...r, documents: (r.documents || []).join(", ") }} onSubmit={(v) => edit(r, v)} submitLabel="Update"
          trigger={<Button size="icon" variant="ghost" className="h-8 w-8" data-testid="loan-product-edit-btn"><Edit className="h-4 w-4" /></Button>} />
      </div>
    ) }] : []),
  ];
  return (
    <div>
      <PageHeader title="Loan Products" subtitle="Configurable loan product master" icon={Package}
        actions={hasPerm("loan_products:add") && (
          <FormDialog title="Add Loan Product" fields={fields} onSubmit={create} submitLabel="Add Product" testId="create-loan-product-dialog"
            trigger={<Button data-testid="create-loan-product-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Product</Button>} />
        )} />
      <DataTable columns={columns} rows={items} loading={loading} rowTestId="loan-product-table-row" />
    </div>
  );
}
